import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ClientsApi,
  ClientWatcherApi,
  ClientType,
  type MaritalStatus,
  type ServiceLine,
  type UpdateClientDto,
  type ClientDto,
  type ClientSearchResultDto,
  type ClientSearchResultDtoPaginatedResponse,
  type ClientSearchSuggestionDto,
} from '@ata-digital/client-tax-system-api-client';
import { createApiConfig, apiAxiosInstance } from '@/lib/api/config';
import { useAuthState } from '@/hooks/auth/useAuth';
import type { ClientFilters } from '@/types/api';
import {
  clientQueryOptions,
  clientSearchQueryOptions,
  clientKeys as centralizedClientKeys,
  taxBinderKeys,
  type ClientSearchFilters,
} from '@/lib/query/options';
import { createLogger } from '@/utils/logger';

const log = createLogger('useClients');

/** Data shape accepted by the update mutation (matches ClientForm schema fields) */
interface UpdateClientData {
  name?: string;
  email?: string;
  phoneNumber?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  type?: string;
  maritalStatus?: string;
  taxId?: string;
  spouseTaxId?: string;
  notes?: string;
  isConfidential?: boolean;
  serviceLines?: ServiceLine[];
}

export const clientKeys = {
  all: ['clients'] as const,
  lists: () => [...clientKeys.all, 'list'] as const,
  list: (filters: ClientFilters) => [...clientKeys.lists(), filters] as const,
  details: () => [...clientKeys.all, 'detail'] as const,
  detail: (id: string) => [...clientKeys.details(), id] as const,
  search: () => [...clientKeys.all, 'search'] as const,
  searchList: (filters: ClientSearchFilters) => [...clientKeys.search(), filters] as const,
} as const;

/**
 * Hook for fetching clients using semantic search
 * Uses the hybrid search endpoint with semantic, fuzzy, and exact matching
 * Returns ClientSearchResultDto[] with nested ClientDto and search scores
 */
export function useClients(params: ClientFilters = {}) {
  const { data: authState } = useAuthState();

  // Convert ClientFilters to ClientSearchFilters format
  const searchFilters: ClientSearchFilters = {
    searchTerm: params.search || '',
    type: params.type,
    officeId: params.officeId,
    officeIds: params.officeIds,
    partnerId: params.partnerId,
    state: params.state,
    enableSemanticSearch: params.enableSemanticSearch ?? true,
    watchedClientsOnly: params.watchedClientsOnly,
    binderStatus: params.binderStatus,
    dateRange: params.dateRange,
    confidentialFilter: params.confidentialFilter,
    page: params.page || 1,
    pageSize: params.limit || 25,
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  };

  return useQuery({
    ...clientSearchQueryOptions.search(searchFilters, authState?.token || undefined),
    enabled: !!authState?.isAuthenticated && !!authState?.token,
  });
}

/**
 * Hook for client search suggestions (autocomplete)
 * Returns ClientSearchSuggestionDto[] for typeahead functionality
 */
export function useClientSuggestions(query: string, limit: number = 10) {
  const { data: authState } = useAuthState();

  return useQuery({
    ...clientSearchQueryOptions.suggestions(query, limit, authState?.token || undefined),
    enabled: !!authState?.isAuthenticated && !!authState?.token && query.length >= 2,
  });
}

// Re-export types for convenience
export type { ClientSearchResultDto, ClientSearchResultDtoPaginatedResponse, ClientSearchSuggestionDto };

/**
 * Hook for fetching detailed client information by ID
 * Uses centralized query options for consistency
 * Returns ClientDetailsDto with full client details including address, contacts, statistics
 */
export function useClient(id: string) {
  const { data: authState } = useAuthState();

  return useQuery({
    ...clientQueryOptions.detail(id, authState?.token || undefined),
    enabled: !!authState?.isAuthenticated && !!authState?.token && !!id,
  });
}

/**
 * Hook for updating an existing client via the real API.
 * Calls PUT /api/clients/{id} with an UpdateClientDto payload.
 */
export function useUpdateClient(id: string) {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();

  return useMutation({
    mutationFn: async (clientData: UpdateClientData): Promise<ClientDto> => {
      const clientsApi = new ClientsApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

      // Map form type string to API enum
      let typeEnum: ClientType | undefined;
      if (clientData.type) {
        typeEnum = clientData.type as ClientType;
      }

      const updateClientDto: UpdateClientDto = {
        name: clientData.name,
        email: clientData.email,
        phoneNumber: clientData.phoneNumber || undefined,
        address1: clientData.address1,
        address2: clientData.address2,
        city: clientData.city,
        state: clientData.state,
        zipCode: clientData.zipCode,
        type: typeEnum,
        maritalStatus: (clientData.maritalStatus || undefined) as MaritalStatus | undefined,
        taxId: clientData.taxId || undefined,
        spouseTaxId: clientData.spouseTaxId || undefined,
        notes: clientData.notes,
        isConfidential: clientData.isConfidential,
        serviceLines: clientData.serviceLines,
      };

      const response = await clientsApi.apiClientsIdPut({ id, updateClientDto });
      const clientDto = response.data?.data;
      if (!clientDto) {
        throw new Error('Failed to update client: No data returned');
      }
      return clientDto;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: clientKeys.search() });
    },
    onError: (error) => {
      log.error('Failed to update client:', error);
    },
  });
}

/**
 * Hook for deleting a client via the real API.
 * Calls DELETE /api/clients/{id}.
 */
export function useDeleteClient() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();

  return useMutation({
    mutationFn: async (clientId: string): Promise<void> => {
      const clientsApi = new ClientsApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);
      await clientsApi.apiClientsIdDelete({ id: clientId });
      log.info('Successfully deleted client:', clientId);
    },
    onSuccess: (_, clientId) => {
      queryClient.removeQueries({ queryKey: clientKeys.detail(clientId) });
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
    },
    onError: (error) => {
      log.error('Failed to delete client:', error);
    },
  });
}

/**
 * Hook for fetching available clients for client switching functionality
 * This is used by client users to see which clients they have access to
 * Returns ClientDto[] with maskedTaxId field for secure display
 */
export function useAvailableClients() {
  const { data: authState } = useAuthState();

  return useQuery({
    ...clientQueryOptions.availableClients(authState?.token || undefined),
    enabled: !!authState?.isAuthenticated && !!authState?.token,
  });
}

// Re-export centralized client keys for client switching
export { centralizedClientKeys as availableClientKeys };

/**
 * Hook for checking if the current user is watching a specific client
 * Returns boolean indicating watch status
 */
export function useIsWatchingClient(clientId: string) {
  const { data: authState } = useAuthState();
  const clientWatcherApi = new ClientWatcherApi(
    createApiConfig(authState?.token || undefined),
    undefined,
    apiAxiosInstance
  );

  return useQuery({
    queryKey: ['clients', 'watchers', 'me', clientId] as const,
    queryFn: async (): Promise<boolean> => {
      const response = await clientWatcherApi.apiClientsClientIdWatchersMeGet({ clientId });
      // API returns BooleanApiResponse with structure: { success?, data?, error?, meta? }
      // response.data.data contains the boolean value
      return response.data?.data || false;
    },
    enabled: !!authState?.isAuthenticated && !!authState?.token && !!clientId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook for fetching all watched client IDs for the current user
 * Returns array of client IDs (GUIDs)
 */
export function useWatchedClients() {
  const { data: authState } = useAuthState();
  const clientWatcherApi = new ClientWatcherApi(
    createApiConfig(authState?.token || undefined),
    undefined,
    apiAxiosInstance
  );

  return useQuery({
    queryKey: ['clients', 'watched'] as const,
    queryFn: async (): Promise<string[]> => {
      const response = await clientWatcherApi.apiClientsWatchedGet();
      // API returns GuidListApiResponse with structure: { success?, data?, error?, meta? }
      // response.data.data contains the array of GUIDs
      return response.data?.data || [];
    },
    enabled: !!authState?.isAuthenticated && !!authState?.token,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook for adding a watcher (following) a client
 * Implements optimistic updates to immediately update the UI
 * Invalidates watched clients list and client lists on success
 */
export function useWatchClient() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const clientWatcherApi = new ClientWatcherApi(
    createApiConfig(authState?.token || undefined),
    undefined,
    apiAxiosInstance
  );

  return useMutation({
    mutationFn: async (clientId: string): Promise<boolean> => {
      const response = await clientWatcherApi.apiClientsClientIdWatchersPost({ clientId });
      // API returns BooleanApiResponse with structure: { success?, data?, error?, meta? }
      return response.data?.data || false;
    },
    onMutate: async (clientId: string) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['clients', 'watchers', 'me', clientId] });
      await queryClient.cancelQueries({ queryKey: ['clients', 'watched'] });

      // Snapshot the previous values
      const previousWatchStatus = queryClient.getQueryData(['clients', 'watchers', 'me', clientId]);
      const previousWatchedList = queryClient.getQueryData(['clients', 'watched']);

      // Optimistically update to the new value
      queryClient.setQueryData(['clients', 'watchers', 'me', clientId], true);

      // Add to watched list if it exists
      if (Array.isArray(previousWatchedList)) {
        queryClient.setQueryData(['clients', 'watched'], [...previousWatchedList, clientId]);
      }

      // Return context with previous values for rollback
      return { previousWatchStatus, previousWatchedList, clientId };
    },
    onError: (_error, _clientId, context) => {
      // Rollback on error
      if (context) {
        queryClient.setQueryData(['clients', 'watchers', 'me', context.clientId], context.previousWatchStatus);
        if (context.previousWatchedList) {
          queryClient.setQueryData(['clients', 'watched'], context.previousWatchedList);
        }
      }
    },
    onSuccess: () => {
      // Invalidate and refetch watched clients list, client search results, and binder lists
      queryClient.invalidateQueries({ queryKey: ['clients', 'watched'] });
      queryClient.invalidateQueries({ queryKey: clientKeys.search() });
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
    },
  });
}

/**
 * Hook for removing a watcher (unfollowing) a client
 * Implements optimistic updates to immediately update the UI
 * Invalidates watched clients list and client lists on success
 */
export function useUnwatchClient() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const clientWatcherApi = new ClientWatcherApi(
    createApiConfig(authState?.token || undefined),
    undefined,
    apiAxiosInstance
  );

  return useMutation({
    mutationFn: async (clientId: string): Promise<boolean> => {
      const response = await clientWatcherApi.apiClientsClientIdWatchersDelete({ clientId });
      // API returns BooleanApiResponse with structure: { success?, data?, error?, meta? }
      return response.data?.data || false;
    },
    onMutate: async (clientId: string) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['clients', 'watchers', 'me', clientId] });
      await queryClient.cancelQueries({ queryKey: ['clients', 'watched'] });

      // Snapshot the previous values
      const previousWatchStatus = queryClient.getQueryData(['clients', 'watchers', 'me', clientId]);
      const previousWatchedList = queryClient.getQueryData(['clients', 'watched']);

      // Optimistically update to the new value
      queryClient.setQueryData(['clients', 'watchers', 'me', clientId], false);

      // Remove from watched list if it exists
      if (Array.isArray(previousWatchedList)) {
        queryClient.setQueryData(
          ['clients', 'watched'],
          previousWatchedList.filter((id: string) => id !== clientId)
        );
      }

      // Return context with previous values for rollback
      return { previousWatchStatus, previousWatchedList, clientId };
    },
    onError: (_error, _clientId, context) => {
      // Rollback on error
      if (context) {
        queryClient.setQueryData(['clients', 'watchers', 'me', context.clientId], context.previousWatchStatus);
        if (context.previousWatchedList) {
          queryClient.setQueryData(['clients', 'watched'], context.previousWatchedList);
        }
      }
    },
    onSuccess: () => {
      // Invalidate and refetch watched clients list, client search results, and binder lists
      queryClient.invalidateQueries({ queryKey: ['clients', 'watched'] });
      queryClient.invalidateQueries({ queryKey: clientKeys.search() });
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
    },
  });
}

/**
 * Hook for bulk following multiple clients
 * Adds the current user as a watcher for multiple clients in one operation
 */
export function useBulkWatchClients() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const clientWatcherApi = new ClientWatcherApi(
    createApiConfig(authState?.token || undefined),
    undefined,
    apiAxiosInstance
  );

  return useMutation({
    mutationFn: async (clientIds: string[]): Promise<number> => {
      log.debug('Bulk watching clients:', clientIds);
      const response = await clientWatcherApi.apiClientsWatchersBulkPost({
        requestBody: clientIds,
      });
      log.debug('Bulk watch response:', response.data);
      // API returns IntApiResponse with structure: { success?, data?, error?, meta? }
      const count = response.data?.data || 0;
      log.info(`Bulk watch completed: ${count} clients added`);
      return count;
    },
    onSuccess: (count) => {
      // Invalidate watched clients list, client lists, and binder lists
      queryClient.invalidateQueries({ queryKey: ['clients', 'watched'] });
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
      log.info(`Successfully added ${count} clients to watch list`);
    },
    onError: (error) => {
      log.error('Error bulk watching clients:', error);
    },
  });
}

/**
 * Hook for bulk unfollowing multiple clients
 * Removes the current user as a watcher for multiple clients in one operation
 */
export function useBulkUnwatchClients() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const clientWatcherApi = new ClientWatcherApi(
    createApiConfig(authState?.token || undefined),
    undefined,
    apiAxiosInstance
  );

  return useMutation({
    mutationFn: async (clientIds: string[]): Promise<number> => {
      log.debug('Bulk unwatching clients:', clientIds);
      const response = await clientWatcherApi.apiClientsWatchersBulkDelete({
        requestBody: clientIds,
      });
      log.debug('Bulk unwatch response:', response.data);
      // API returns IntApiResponse with structure: { success?, data?, error?, meta? }
      const count = response.data?.data || 0;
      log.info(`Bulk unwatch completed: ${count} clients removed`);
      return count;
    },
    onSuccess: (count) => {
      // Invalidate watched clients list, client lists, and binder lists
      queryClient.invalidateQueries({ queryKey: ['clients', 'watched'] });
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
      log.info(`Successfully removed ${count} clients from watch list`);
    },
    onError: (error) => {
      log.error('Error bulk unwatching clients:', error);
    },
  });
}
